import { cents, percentage } from '../util/formatters'

import { State } from '../constants'

export interface BulkActionLog {
    id: number
    user_id: number

    actions: { [key: number]: any } | null
    filters: any
    trigger: any
    query: string | null
    effected: number[]

    for: number
    for_string: string

    created_at: string
    updated_at: string
}

export enum BulkActionLogAction {
    ChangeState = 1,
    ChangeDefaultBid = 2,
    ChangeDefaultBidPercent = 3,
    ChangeDailyBudget = 4,
    ChangeDailyBudgetPercent = 5,
    ChangeBid = 6,
    ChangeBidPercent = 7,
    ChangeStartDate = 8,
    ChangeEndDate = 9,
    ChangeName = 10,
    ChangePremiumBidAdjustment = 11,
    ChangeTacos = 12,
    ChangeAssignedPortfolio = 13,
    DeletePortfolio = 14,
    CreateNegativeKeyword = 15,
    ChangeDefaultBidDelta = 16,
    ChangeDailyBudgetDelta = 17,
    ChangeBidDelta = 18,
    ChangeSmartBid = 19,
    AddAsinToAdGroup = 20,
    ChangeBidStrategy = 21,
    ChangeTosPlacement = 22,
    ChangeRosPlacement = 23,
    ChangePpPlacement = 24,
}

export const ActionsToText = (log: BulkActionLog): string[] => {
    if (!log.actions) {
        return []
    }

    const out: string[] = []
    Object.keys(log.actions).map((action) => {
        const v = log.actions?.[parseInt(action)]
        out.push(ActionToText(parseInt(action), v))
    })

    return out
}

export const resolveState = (state: number): string => {
    switch (state) {
        case State.Paused:
            return 'paused'
        case State.Archived:
            return 'archived'
        case State.Enabled:
            return 'enabled'
        default:
            console.warn('Cannot resolve state for', state)
            return ''
    }
}

export const ActionToText = (action: BulkActionLogAction, value: any): string => {
    let dir = null
    if (!isNaN(parseFloat(value))) {
        dir = value > 0 ? 'increase' : 'decrease'
    }

    switch (action) {
        case BulkActionLogAction.ChangeState:
            return `changed state to ${resolveState(value)}`

        case BulkActionLogAction.ChangeDefaultBid:
            return `default bid set to ${cents(Math.abs(value))}`
        case BulkActionLogAction.ChangeDefaultBidDelta:
            return `${dir}d default bid by ${cents(Math.abs(value))}`
        case BulkActionLogAction.ChangeDefaultBidPercent:
            return `${dir}d default bid by ${percentage(Math.abs(value))}`

        case BulkActionLogAction.ChangeDailyBudget:
            return `daily budget set to ${cents(Math.abs(value))}`
        case BulkActionLogAction.ChangeDailyBudgetDelta:
            return `${dir}d daily budget by ${cents(Math.abs(value))}`
        case BulkActionLogAction.ChangeDailyBudgetPercent:
            return `${dir}d daily budget by ${percentage(Math.abs(value))}`

        case BulkActionLogAction.ChangeBid:
            return `bid set to ${cents(Math.abs(value))}`
        case BulkActionLogAction.ChangeBidDelta:
            return `${dir}d bid by ${cents(Math.abs(value))}`
        case BulkActionLogAction.ChangeBidPercent:
            return `${dir}d bid by ${percentage(Math.abs(value))}`

        case BulkActionLogAction.ChangeStartDate:
            return `changed start date to ${resolveState(value)}`
        case BulkActionLogAction.ChangeEndDate:
            return `changed end date to ${resolveState(value)}`
        case BulkActionLogAction.ChangeName:
            return `changed name to ${value}`
        case BulkActionLogAction.ChangePremiumBidAdjustment:
            return `changed premium bid adjustment to ${value ? 'true' : 'false'}`
        case BulkActionLogAction.ChangeTacos:
            return `changed TACoS to ${percentage(value / 100)}`
        case BulkActionLogAction.ChangeAssignedPortfolio:
            return `changed assigned portfolio to ${value}`
        case BulkActionLogAction.DeletePortfolio:
            return `deleted portfolio ${value}`
        case BulkActionLogAction.CreateNegativeKeyword:
            return `created negative keyword "${value.keyword}"`
        case BulkActionLogAction.ChangeSmartBid:
            return `changed smart bid to ${value ? 'true' : 'false'}`
        case BulkActionLogAction.AddAsinToAdGroup:
            return 'added ASIN to ad group'
        case BulkActionLogAction.ChangeBidStrategy:
            return `changed bid strategy to ${value}`
        case BulkActionLogAction.ChangeTosPlacement:
            return `changed top of search placement to ${percentage(value / 100)}`
        case BulkActionLogAction.ChangeRosPlacement:
            return `changed rest of search placement to ${percentage(value / 100)}`
        case BulkActionLogAction.ChangePpPlacement:
            return `changed product pages placement to ${percentage(value / 100)}`
        default:
            console.warn('Cannot resolve action to test for', action, value)
            return ''
    }
}
